import { WritableSignal } from '@angular/core';
import {
  COMPOSITE_SEP,
  SalesStatisticsQueryService,
} from '../services/sales-statistics-query.service';

export type DrillFilterKind = 'plain' | 'composite' | 'customer';

export interface DrillLevel {
  key: string;
  /** Backend id column — the row key where the code is only unique inside its
      parent (sub groups, products). Falls back to `code` when absent. */
  id?: string;
  code: string;
  name: string;
  titleKey: string;
  filterSignal: (q: SalesStatisticsQueryService) => WritableSignal<string[] | null>;
  labelKey: string;
  filterKind: DrillFilterKind;
}

const PRODUCT_LEVEL: DrillLevel = {
  key: 'product',
  id: 'product.id',
  code: 'product.number',
  name: 'product.name',
  titleKey: '#SALES_STATISTICS.CHART.PRODUCTS',
  filterSignal: (q) => q.product,
  labelKey: 'product',
  filterKind: 'plain',
};

export const PRODUCT_GROUP_DRILL: DrillLevel[] = [
  {
    key: 'productGroup',
    code: 'product_group.code',
    name: 'product_group.name',
    titleKey: '#SALES_STATISTICS.CHART.PRODUCT_GROUPS',
    filterSignal: (q) => q.productGroup,
    labelKey: 'productGroup',
    filterKind: 'plain',
  },
  PRODUCT_LEVEL,
];

export const MAIN_GROUP_DRILL: DrillLevel[] = [
  {
    key: 'mainGroup',
    code: 'product_main_group.code',
    name: 'product_main_group_language.name',
    titleKey: '#SALES_STATISTICS.CHART.MAIN_GROUPS',
    filterSignal: (q) => q.mainGroup,
    labelKey: 'mainGroup',
    filterKind: 'composite',
  },
  {
    key: 'subGroup',
    id: 'product_sub_group.id',
    code: 'product_sub_group.code',
    name: 'product_sub_group_language.name',
    titleKey: '#SALES_STATISTICS.CHART.SUB_GROUPS',
    filterSignal: (q) => q.subGroup,
    labelKey: 'subGroup',
    filterKind: 'composite',
  },
  {
    key: 'subSubGroup',
    id: 'product_sub_sub_group.id',
    code: 'product_sub_sub_group.code',
    name: 'product_sub_sub_group_language.name',
    titleKey: '#SALES_STATISTICS.CHART.SUB_SUB_GROUPS',
    filterSignal: (q) => q.subSubGroup,
    labelKey: 'subSubGroup',
    filterKind: 'composite',
  },
  PRODUCT_LEVEL,
];

const CUSTOMER_LEVEL: DrillLevel = {
  key: 'customer',
  code: 'customer.number',
  name: 'customer.name',
  titleKey: '#SALES_STATISTICS.CHART.CUSTOMERS',
  filterSignal: (q) => q.customer,
  labelKey: 'customer',
  filterKind: 'customer',
};

export const CUSTOMER_DRILL: DrillLevel[] = [
  {
    key: 'customerGroup',
    code: 'customer_group.customer_group',
    name: 'customer_group_language.name',
    titleKey: '#SALES_STATISTICS.CHART.CUSTOMER_GROUPS',
    filterSignal: (q) => q.customerGroup,
    labelKey: 'customerGroup',
    filterKind: 'composite',
  },
  CUSTOMER_LEVEL,
];

export const VERTRETER_DRILL: DrillLevel[] = [
  {
    key: 'salesRepresentative',
    code: 'sales_representative.sales_representative_number',
    name: 'sales_representative.name',
    titleKey: '#SALES_STATISTICS.CHART.SALES_REPRESENTATIVES',
    filterSignal: (q) => q.salesRepresentative,
    labelKey: 'salesRepresentative',
    filterKind: 'plain',
  },
];

export function drillFilterValue(level: DrillLevel, code: string, name: string): string {
  switch (level.filterKind) {
    case 'composite':
      return `${code}${COMPOSITE_SEP}${name}`;
    case 'customer':
      return code;
    default:
      return code;
  }
}
